import { Injectable, inject } from '@angular/core';
import {combineLatest, from, lastValueFrom, Observable, of} from 'rxjs';
import {TenantService} from './tenant.service';
import {CourseSection} from '../models/course-section.model';
import {createFirestoreId, findLastBySeqNo, findUniqueMatchWithId, readCollectionWithIds, readDocumentWithId} from '../common/firestore-utils';
import {catchError, concatMap, first, map} from 'rxjs/operators';
import {Lesson} from '../models/lesson.model';
import {Course} from '../models/course.model';
import {Update} from '@ngrx/entity';
import {increment} from '@firebase/firestore';
import { collection, deleteDoc, doc, Firestore, getAggregateFromServer, getCountFromServer, limit, orderBy, query, setDoc, sum, updateDoc, where, writeBatch } from '@angular/fire/firestore';
import { FirestoreService } from './firestore.service';

/**
 * Whole-course published-lesson counts. Deliberately independent of the section
 * pagination the landing page and the embed widget use, which is why they exist:
 * counting the lessons that happen to be loaded reports the first page only.
 */
export interface PublishedLessonStats {
  total: number;
  sections: number;
  textOnly: number;
  audios: number;
  quizzes: number;
  downloads: number;
  assignments: number;
  /**
   * Published lessons of type `video`, including externally hosted ones (YouTube, Vimeo),
   * since those are still video lessons. Used to decide whether a duration may be
   * described as "on-demand video" - a coaching or text product that sets a duration
   * manually has one, but it is not video.
   */
  videos: number;
  /**
   * Summed `videoDuration` of the published video lessons, in seconds, straight from
   * Firestore.
   *
   * The course document carries a `totalDuration` that is maintained by hand as lessons are
   * added and removed, and it drifts: on the two courses this was reported for it read 8 and
   * 35 against a real 27. When it drifts to zero the sales page advertises "0 min Video" on a
   * course that plainly has video. Summing the lessons themselves cannot drift.
   */
  videoDurationSeconds: number;
}

export const EMPTY_PUBLISHED_LESSON_STATS: PublishedLessonStats = {
  total: 0, sections: 0, textOnly: 0, audios: 0, quizzes: 0, downloads: 0, assignments: 0, videos: 0,
  videoDurationSeconds: 0
};

@Injectable()
export class LessonsDBService {
  private tenant = inject(TenantService);
  private firestore = inject(Firestore);
  private fs = inject(FirestoreService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);


  constructor() {

  }

  findLessonById(courseId:string, lessonId:string): Observable<Lesson> {

    const lessonPath = this.lessonsPath(courseId) + `/${lessonId}`;

    return readDocumentWithId<Lesson>(this.fs.docData(doc(this.firestore, lessonPath), {idField: "id"})).pipe(first());
  }


  loadCourseSection(courseId:string, sectionId:string) {
    return readDocumentWithId(this.fs.docData(doc(this.firestore,this.sectionsPath(courseId) + '/' + sectionId ), {idField: "id"})).pipe(first());

  }

  loadCourse(courseId: string) {

    return readDocumentWithId(this.fs.docData(doc(this.firestore, this.tenant.path(`courses/${courseId}`) ), {idField: "id"})).pipe(first());

  }


  loadCourseSections(courseId: string): Observable<CourseSection[]> {

    const coursePath = this.tenant.path(`courses/${courseId}`);

    const constraints = [
      orderBy('seqNo')
    ]

    const courseSectionsQuery = query(collection(this.firestore, coursePath + '/sections'), ...constraints);

    // Use getDocs for one-time snapshot read instead of persistent listener
    // This prevents interference with loadAllVisibleLiveSessionsAndWebinars()
    const courseSectionsData = from(this.fs.getDocs(courseSectionsQuery)).pipe(
      map((snapshot) => {
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }),
      first() // Ensure it completes after one emission
    );

    return readCollectionWithIds<CourseSection[]>(
      courseSectionsData
      )
      .pipe(
      map(sections => sections.map(section => {return {...section, courseId}}))
      );

  }

  loadCourseLessons(courseId: string): Observable<Lesson[]> {

    const coursePath = this.tenant.path(`courses/${courseId}`);

    const constraints = [
      orderBy('seqNo')
    ]

    // Use getDocs for one-time snapshot read instead of persistent listener
    // This prevents interference with loadAllVisibleLiveSessionsAndWebinars()
    const lessonsQuery = query(collection(this.firestore, coursePath + '/lessons'), ...constraints);

    const courseLessonsData = from(this.fs.getDocs(lessonsQuery)).pipe(
      map((snapshot) => {
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }),
      first() // Ensure it completes after one emission
    );

    return readCollectionWithIds<Lesson[]>(courseLessonsData);

    // return readCollectionWithIds<Lesson[]>(this.afs.collection(coursePath + '/lessons', ref => ref.orderBy('seqNo')));

  }

  loadCoursesLessonsCount(courseId: string): Observable<string> {
    const coursePath = this.tenant.path(`courses/${courseId}`);
    const q = query(
      collection(this.firestore, coursePath + '/lessons'),
      where('status', '==', 'published')
    );
    return from(this.fs.getDocs(q)).pipe(
      map(snapshot => snapshot.size.toString())
    );
  }

  /**
   * Published-lesson counts for the WHOLE course, plus the total section count.
   *
   * The landing page and the embed widget only load the first page of sections, so
   * anything derived from the lessons currently in the store reports the first page
   * only — that is what made a 26-lesson course advertise "12 total lessons".
   *
   * Uses Firestore count aggregates rather than reading the documents, so the whole
   * set of numbers costs a handful of reads regardless of how big the course is.
   * Every filter is an equality filter, so no composite index is needed.
   */
  loadPublishedLessonStats(courseId: string): Observable<PublishedLessonStats> {
    const coursePath = this.tenant.path(`courses/${courseId}`);
    const lessonsRef = collection(this.firestore, coursePath + '/lessons');
    const publishedConstraint = where('status', '==', 'published');

    const countPublished = (type?: string): Observable<number> => {
      const q = type
        ? query(lessonsRef, publishedConstraint, where('type', '==', type))
        : query(lessonsRef, publishedConstraint);
      return from(getCountFromServer(q)).pipe(map(snapshot => snapshot.data().count));
    };

    const sections$ = from(
      getCountFromServer(query(collection(this.firestore, coursePath + '/sections')))
    ).pipe(map(snapshot => snapshot.data().count));

    // Real total video length, summed by Firestore itself rather than read from the course
    // document's hand-maintained totalDuration. Same aggregate mechanism as the counts above,
    // so it costs a handful of reads no matter how long the course is.
    //
    // Unlike a plain count, summing a third field needs a composite index
    // (status, type, videoDuration) - see firestore.indexes.json. Until that index finishes
    // building Firestore answers FAILED_PRECONDITION, and an error here would travel through
    // the combineLatest below and take every other figure with it, blanking the whole
    // curriculum summary. Degrade to 0 instead, which simply falls back to the stored
    // totalDuration at the call site.
    const videoDurationSeconds$ = from(
      getAggregateFromServer(
        query(lessonsRef, publishedConstraint, where('type', '==', 'video')),
        { seconds: sum('videoDuration') }
      )
    ).pipe(
      map(snapshot => snapshot.data().seconds ?? 0),
      catchError(error => {
        console.warn('Could not sum video duration for this course, falling back', error);
        return of(0);
      })
    );

    return combineLatest([
      countPublished(),
      sections$,
      countPublished('text-only'),
      countPublished('audio'),
      countPublished('quiz'),
      countPublished('download'),
      countPublished('assignment'),
      countPublished('video'),
      videoDurationSeconds$,
    ]).pipe(
      map(([total, sections, textOnly, audios, quizzes, downloads, assignments, videos, videoDurationSeconds]) => ({
        total, sections, textOnly, audios, quizzes, downloads, assignments, videos, videoDurationSeconds
      })),
      first()
    );
  }

  // Published lesson IDs across the whole course (all sections, not paginated).
  // Same query/cost as loadCoursesLessonsCount — reads only published docs.
  //
  // Only counts lessons the player can actually REACH. The player renders lessons grouped
  // by section (calculateSectionLessons), so a published lesson whose sectionId points at a
  // section that no longer exists appears nowhere - deleteSection removes the section doc
  // and leaves its lessons behind. Those lessons used to sit in this set anyway, which made
  // the progress denominator bigger than the number of lessons a student can possibly
  // complete: finish everything visible and you stop at 98%, with "1 lesson to go" and no
  // lesson to go to. Artificial course-completion entries are excluded for the same reason -
  // calculateSectionLessons filters them out, so they are not completable either.
  loadPublishedLessonIds(courseId: string): Observable<string[]> {
    const coursePath = this.tenant.path(`courses/${courseId}`);
    const publishedLessons$ = from(
      this.fs.getDocs(
        query(collection(this.firestore, coursePath + '/lessons'), where('status', '==', 'published'))
      )
    );
    const sections$ = from(this.fs.getDocs(collection(this.firestore, coursePath + '/sections')));

    return combineLatest([publishedLessons$, sections$]).pipe(
      map(([lessonsSnap, sectionsSnap]) => {
        const sectionIds = new Set(sectionsSnap.docs.map(doc => doc.id));
        return lessonsSnap.docs
          .filter(doc => {
            const lesson = doc.data() as Partial<Lesson>;
            return (
              lesson.type !== 'course-completion' &&
              !!lesson.sectionId &&
              sectionIds.has(lesson.sectionId)
            );
          })
          .map(doc => doc.id);
      }),
      first()
    );
  }

  loadAllVisibleLiveSessionsAndWebinars(): Observable<any[]> {
    const coursesPath = this.tenant.path('courses');
    const coursesQuery = query(
      collection(this.firestore, coursesPath),
      where('status', '==', 'published')
    );

    return this.fs.collectionData(coursesQuery, { idField: 'id' }).pipe(
      concatMap((courses: any[]) => {
        // Only include courses where reminders are not explicitly hidden
        const visibleCourses = (courses || []).filter(
          (course) => course.hideLiveSessionsAndWebinarsReminders !== true
        );

        if (!visibleCourses.length) {
          return from([[]]);
        }

        const lessonObservables = visibleCourses.map((course) => {
          const lessonsPath = this.tenant.path(`courses/${course.id}/lessons`);
          const sectionsPath = this.tenant.path(`courses/${course.id}/sections`);

          const lessons$ = this.fs.collectionData(
            query(
              collection(this.firestore, lessonsPath),
              where('type', 'in', ['live-session', 'webinar']),
              where('status', '==', 'published')
            ),
            { idField: 'id' }
          );

          const sections$ = this.fs.collectionData(
            query(collection(this.firestore, sectionsPath)),
            { idField: 'id' }
          );

          return combineLatest([lessons$, sections$]).pipe(
            map(([lessons, sections]: [any[], any[]]) => {
              return lessons.map((lesson) => {
                const section = sections.find(s => s.id === lesson.sectionId);
                return {
                  ...lesson,
                  courseId: course.id,
                  courseTitle: course.title,
                  courseUrl: course.url,
                  sectionSeqNo: section?.seqNo || 1
                };
              });
            })
          );
        });

        return combineLatest(
          lessonObservables.length > 0 ? lessonObservables : [from([[]])]
        );
      }),
      map((lessonsArrays) => {
        const flattened: any[] = [];
        lessonsArrays.forEach(arr => flattened.push(...arr));
        return flattened;
      })
    );
  }

  loadAllLiveSessions(): Observable<any[]> {
    const coursesPath = this.tenant.path('courses');
    const coursesQuery = query(
      collection(this.firestore, coursesPath),
      where('status', '==', 'published')
    );

    return this.fs.collectionData(coursesQuery, { idField: 'id' }).pipe(
      concatMap((courses: any[]) => {
        if (!courses.length) {
          return from([[]]);
        }

        const lessonObservables = courses.map((course) => {
          const lessonsPath = this.tenant.path(`courses/${course.id}/lessons`);
          const sectionsPath = this.tenant.path(`courses/${course.id}/sections`);

          const lessons$ = this.fs.collectionData(
            query(
              collection(this.firestore, lessonsPath),
              where('type', 'in', ['live-session', 'webinar']),
              where('status', '==', 'published')
            ),
            { idField: 'id' }
          );

          const sections$ = this.fs.collectionData(
            query(collection(this.firestore, sectionsPath)),
            { idField: 'id' }
          );

          return combineLatest([lessons$, sections$]).pipe(
            map(([lessons, sections]: [any[], any[]]) => {
              return lessons.map((lesson) => {
                const section = sections.find(s => s.id === lesson.sectionId);
                return {
                  ...lesson,
                  courseId: course.id,
                  courseTitle: course.title,
                  courseUrl: course.url,
                  sectionSeqNo: section?.seqNo || 1
                };
              });
            })
          );
        });

        return combineLatest(lessonObservables.length > 0 ? lessonObservables : [from([[]])]);
      }),
      map((lessonsArrays) => {
        const flattened: any[] = [];
        lessonsArrays.forEach(arr => flattened.push(...arr));
        return flattened;
      })
    );
  }

  async loadCoursesLessonsPublishedCount(coursesIds: string[]){

    let count = 0;

    for (const courseId of coursesIds) {

      const coursePath = this.tenant.path(`courses/${courseId}`);
      const constraints = [
        where("status", "==", "published"),
      ]

      const courseLessonsData = await lastValueFrom(
        this.fs.collectionData(query(collection(this.firestore, coursePath + '/lessons'), ...constraints), {idField: "id"}).pipe(first())
    )

      count += courseLessonsData.length

    }

    return count

  }


  loadCourseSectionsPage(courseId: string, afterSeqNo: number, pageSize: number): Observable<CourseSection[]> {
    const sectionsQuery = query(
      collection(this.firestore, this.sectionsPath(courseId)),
      orderBy('seqNo'),
      where('seqNo', '>', afterSeqNo),
      limit(pageSize)
    );
    return from(this.fs.getDocs(sectionsQuery)).pipe(
      map(snapshot => snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CourseSection)))
    );
  }

  loadCourseSectionsBefore(courseId: string, beforeSeqNo: number, pageSize: number): Observable<CourseSection[]> {
    const sectionsQuery = query(
      collection(this.firestore, this.sectionsPath(courseId)),
      orderBy('seqNo', 'desc'),
      where('seqNo', '<', beforeSeqNo),
      limit(pageSize)
    );
    return from(this.fs.getDocs(sectionsQuery)).pipe(
      map(snapshot => snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CourseSection)))
    );
  }

  loadSectionBySeqNo(courseId: string, seqNo: number): Observable<CourseSection[]> {
    const sectionsQuery = query(
      collection(this.firestore, this.sectionsPath(courseId)),
      where('seqNo', '==', seqNo),
      limit(1)
    );
    return from(this.fs.getDocs(sectionsQuery)).pipe(
      map(snapshot => snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CourseSection)))
    );
  }

  loadLessonsForSections(courseId: string, sectionIds: string[]): Observable<Lesson[]> {
    if (sectionIds.length === 0) return of([]);

    // Firestore `in` query supports at most 30 values. Chunk the IDs and
    // combine the results so courses with many sections don't throw.
    const CHUNK = 30;
    const chunks: string[][] = [];
    for (let i = 0; i < sectionIds.length; i += CHUNK) {
      chunks.push(sectionIds.slice(i, i + CHUNK));
    }

    const queries$ = chunks.map(chunk =>
      from(this.fs.getDocs(query(
        collection(this.firestore, this.lessonsPath(courseId)),
        where('sectionId', 'in', chunk),
        orderBy('seqNo')
      ))).pipe(
        map(snapshot => snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Lesson)))
      )
    );

    return combineLatest(queries$).pipe(map(arrays => arrays.flat()));
  }

  addNewSection(course: Course, title: string):Observable<CourseSection> {
    return findLastBySeqNo<CourseSection>(this.firestore, this.sectionsPath(course.id))
      .pipe(
        concatMap(last => {

          const newSection: any = {
            title,
            seqNo: last ? (last.seqNo + 1) : 1
          };

            const docRef = doc(this.firestore, this.sectionsPath(course.id) + '/' + createFirestoreId(this.firestore))
            const addSectionAsync = setDoc(
              docRef,
              newSection
            ).then(() => {
              return {
                id: docRef.id,
                courseId: course.id,
                ...newSection
              }
            })

          return from(addSectionAsync);
        })
      );
  }

  saveSection(courseId: string, sectionId:string, update: Update<CourseSection>): Observable<any> {

    const sectionDocRef = doc(this.firestore, this.sectionsPath(courseId), update.id as string)

    const updateRef = updateDoc(sectionDocRef, update.changes)

    return from(updateRef);

  }


  addNewLesson(courseId: string, sectionId: string, title: string, type: string, newLessonSeqNo:number, changes?: Partial<Lesson>, lessonId?: string): Observable<Lesson> {

    const calendarBookingDefaults = type === 'calendar-booking' ? {
      calendarBookingButtonText: "Book Now",
      calendarBookingButtonTextColor: "#ffffff",
      calendarBookingButtonColor: "#EB3B3B",
      calendarBookingButtonIconColor: "#ffffff"
    } : {};

    const newLesson: any = {
      title,
      type,
      seqNo: newLessonSeqNo,
      sectionId,
      status: 'draft',
      free:false,
      ...calendarBookingDefaults,
      ...changes

    };

    let lessonFirestoreId: string;

    if (lessonId) {
      lessonFirestoreId = lessonId;
    } else {
      lessonFirestoreId = createFirestoreId(this.firestore);
    }

    const docRef = doc(this.firestore, this.lessonsPath(courseId), lessonFirestoreId)
    const addLessonAsync = setDoc(docRef, newLesson).then(() => {
      return {
        id: docRef.id,
        ...newLesson
      }
    })

    return from(addLessonAsync);
  }

  saveLesson(courseId: string, update: Update<Lesson>): Observable<any> {

    const saveLessonAsync = updateDoc(doc(this.firestore, this.lessonsPath(courseId), update.id as string), update.changes)

    return from(saveLessonAsync);

  }

  /**
   * Deletes a section together with the lessons inside it.
   *
   * Deleting only the section doc left its lessons in Firestore with sectionId pointing at
   * something that no longer existed. Every view groups lessons by section, so those lessons
   * became invisible in both the player and the edit screen while still counting towards the
   * progress total - students who finished every lesson they could see were capped below
   * 100%, told "1 lesson to go" with no lesson to open. Nothing cleaned them up afterwards:
   * there is no section-deleted trigger.
   *
   * Also gives back the course duration the deleted video lessons contributed, the way
   * deleteLesson does for a single lesson.
   */
  deleteSection(courseId: string, sectionId: string): Observable<any> {

    const lessonsInSection$ = from(
      this.fs.getDocs(
        query(collection(this.firestore, this.lessonsPath(courseId)), where('sectionId', '==', sectionId))
      )
    );

    return lessonsInSection$.pipe(
      concatMap(async lessonsSnap => {
        const totalVideoDuration = lessonsSnap.docs.reduce(
          (sum, lessonDoc) => sum + (lessonDoc.data()?.videoDuration ?? 0),
          0
        );

        // A batch takes at most 500 writes, so commit the lessons in chunks and remove the
        // section last - if a chunk fails the section stays put and the lessons stay
        // reachable, rather than stranding whatever was left.
        const BATCH_LIMIT = 450;
        for (let i = 0; i < lessonsSnap.docs.length; i += BATCH_LIMIT) {
          const batch = writeBatch(this.firestore);
          for (const lessonDoc of lessonsSnap.docs.slice(i, i + BATCH_LIMIT)) {
            batch.delete(lessonDoc.ref);
          }
          await batch.commit();
        }

        const finalBatch = writeBatch(this.firestore);
        finalBatch.delete(doc(this.firestore, this.sectionsPath(courseId), sectionId));
        if (totalVideoDuration) {
          finalBatch.update(doc(this.firestore, `schools/${this.tenant.id}/courses/${courseId}`), {
            totalDuration: increment(-1 * totalVideoDuration)
          });
        }
        await finalBatch.commit();

        return { deletedLessons: lessonsSnap.size };
      })
    );

  }

  deleteLesson(courseId: string, lessonId:string, videoDuration:number): Observable<any> {

    const batch = writeBatch(this.firestore);

    const lessonRef = doc(this.firestore,this.lessonsPath(courseId), lessonId)

    batch.delete(lessonRef);

    if (videoDuration) {
      const courseRef = doc(this.firestore, `schools/${this.tenant.id}/courses/${courseId}`);

      batch.update(courseRef, {
        totalDuration: increment(-1 * videoDuration)
      });
    }

    return from(batch.commit());
  }

  private findLastLessonInSection(courseId:string, sectionId:string): Observable<Lesson> {

    const constraints = [
      where('sectionId', '==', sectionId),
      orderBy('seqNo', 'desc'),
      limit(1)
    ]

    const query$ = this.fs.collectionData(
      query(
        collection(this.firestore, this.lessonsPath(courseId)), ...constraints),
      {idField: "id"}
      )

    // const query$ = this.afs.collection<Lesson>(this.lessonsPath(courseId),
    //     ref => ref.where('sectionId', '==', sectionId).orderBy('seqNo', 'desc').send-limit-email(1));

    return findUniqueMatchWithId(query$).pipe(first()) as Observable<Lesson>;
  }


  suscribeToLesson(courseId: string, lessonId:string): Observable<Lesson> {
    const lessonsData = this.fs.docData(doc(this.firestore, this.lessonsPath(courseId) + '/' + lessonId), {idField: "id"})
    return readDocumentWithId(lessonsData);
  }


  private sectionsPath(courseId:string) {
    return this.tenant.path(`courses/${courseId}/sections`);
  }

  private lessonsPath(courseId:string) {
    return this.tenant.path(`courses/${courseId}/lessons`);
  }

  addLessonsSections(courseId:string, lessons: Lesson[], sections: CourseSection[]) {

    const batch = writeBatch(this.firestore);

    lessons.forEach(lesson => {
      const lessonRef = doc(this.firestore, this.lessonsPath(courseId), lesson.id);
      // batch.set(lessonRef, lesson);
      batch.set(lessonRef, lesson, { merge: true });

    });

    sections.forEach(section => {
      const sectionRef = doc(this.firestore, this.sectionsPath(courseId), section.id);
      batch.set(sectionRef, section);
    });

    return from(batch.commit());

  }

  updateLessons(courseId:string, changes: Update<Lesson>[]) {

    const batch = writeBatch(this.firestore);

    changes.forEach(change => {
      const lessonRef = doc(this.firestore, `schools/${this.tenant.id}/courses/${courseId}/lessons/${change.id}`);
      batch.update(lessonRef, change.changes);
    });

    return from(batch.commit());

  }
}




